import React from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Loader2, Link2, Copy, Coins, CheckCircle2 } from 'lucide-react';
import { LEGALCOOP_OFFICIAL_DEFINITION } from '../constants/legalcoop';

export default function LegalcoopModals({
  actionModal, setActionModal, actionInput, setActionInput, actionSubmitting, submitAction,
  linkModal, setLinkModal, linkResult, setLinkResult, linkEmail, setLinkEmail, linkNote, setLinkNote,
  linkSubmitting, submitPaymentLink, copyToClipboard,
  creditConfigModal, setCreditConfigModal, creditConfig, setCreditConfig, creditRoles,
  creditConfigLoading, creditConfigSaving, saveCreditConfig, setServiceRule, toggleExemptRole,
}) {
  return (
    <>
      {/* Modal action */}
      <Dialog open={!!actionModal} onOpenChange={(o) => { if (!o) { setActionModal(null); setActionInput(''); } }}>
        <DialogContent data-testid="legalcoop-action-modal">
          <DialogHeader>
            <DialogTitle>
              {actionModal?.kind === 'mark_paid' && 'Marquer comme payé'}
              {actionModal?.kind === 'exempt' && 'Exonérer la demande'}
              {actionModal?.kind === 'assign' && 'Assigner un juriste'}
              {actionModal?.kind === 'complete' && 'Marquer revue terminée'}
              {actionModal?.kind === 'stripe' && 'Générer lien de paiement Stripe'}
              {actionModal?.kind === 'invoice' && 'Télécharger la facture PDF'}
            </DialogTitle>
            <DialogDescription>
              <p className="text-[11px] text-slate-600 italic leading-relaxed mb-2" data-testid="legalcoop-modal-definition">
                {LEGALCOOP_OFFICIAL_DEFINITION}
              </p>
              {actionModal?.kind === 'stripe' && (
                <span>Un lien sécurisé Stripe va être généré (one-shot 500 € HT = 600 € TTC). Cliquez pour ouvrir dans un nouvel onglet.</span>
              )}
              {actionModal?.kind === 'exempt' && (
                <span className="text-red-700">Justification d&apos;au moins 5 caractères obligatoire.</span>
              )}
              {actionModal?.kind === 'assign' && (
                <span>Saisir l&apos;email du juriste à assigner.</span>
              )}
            </DialogDescription>
          </DialogHeader>
          {actionModal?.kind !== 'stripe' && actionModal?.kind !== 'invoice' && (
            actionModal?.kind === 'assign' ? (
              <Input
                placeholder="email.juriste@example.fr"
                value={actionInput}
                onChange={(e) => setActionInput(e.target.value)}
                data-testid="legalcoop-action-input"
              />
            ) : (
              <Textarea
                rows={3}
                placeholder={
                  actionModal?.kind === 'exempt' ? 'Raison de l\'exonération (min 5 caractères)' :
                  actionModal?.kind === 'complete' ? 'Note de clôture (optionnelle)' :
                  'Note (optionnelle)'
                }
                value={actionInput}
                onChange={(e) => setActionInput(e.target.value)}
                data-testid="legalcoop-action-input"
              />
            )
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionModal(null); setActionInput(''); }} disabled={actionSubmitting}>
              Annuler
            </Button>
            <Button onClick={submitAction} disabled={actionSubmitting} data-testid="legalcoop-action-submit">
              {actionSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V37.61 — Modal : generer un lien Stripe par email */}
      <Dialog open={linkModal} onOpenChange={(o) => { if (!o) { setLinkModal(false); setLinkResult(null); } }}>
        <DialogContent data-testid="legalcoop-link-modal" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-indigo-700" />
              Générer un lien de paiement Stripe par email
            </DialogTitle>
            <DialogDescription>
              <p className="text-[11px] text-slate-600 italic leading-relaxed mb-2">
                {LEGALCOOP_OFFICIAL_DEFINITION}
              </p>
              <span className="text-slate-700">
                Saisir l&apos;email du membre. Une demande LEGALCOOP <b>pending_payment</b> est créée et un lien
                Stripe Checkout pré-rempli (one-shot 500 € HT) est généré. Copiez et envoyez le lien au membre.
              </span>
            </DialogDescription>
          </DialogHeader>

          {!linkResult ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Email du membre *</label>
                <Input
                  type="email"
                  placeholder="membre@example.fr"
                  value={linkEmail}
                  onChange={(e) => setLinkEmail(e.target.value)}
                  data-testid="legalcoop-link-email-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Note interne (optionnelle)</label>
                <Textarea
                  rows={2}
                  placeholder="Ex : envoyé suite à demande téléphonique du membre"
                  value={linkNote}
                  onChange={(e) => setLinkNote(e.target.value)}
                  data-testid="legalcoop-link-note-input"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="legalcoop-link-result">
              <div className="bg-emerald-50 border border-emerald-300 rounded p-3 space-y-2">
                <p className="text-sm text-emerald-900 font-semibold">
                  ✓ Lien Stripe généré pour <b>{linkResult.customer_email}</b>
                </p>
                <p className="text-xs text-slate-700">
                  Montant : <b>{linkResult.amount_ttc} € TTC</b> · Demande :{' '}
                  <span className="font-mono">{linkResult.request_id?.slice(0, 8)}</span>
                </p>
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Lien à envoyer au membre :</label>
                <div className="flex gap-1">
                  <Input
                    readOnly
                    value={linkResult.checkout_url || ''}
                    className="font-mono text-xs"
                    data-testid="legalcoop-link-url-input"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button
                    variant="outline"
                    onClick={() => copyToClipboard(linkResult.checkout_url)}
                    data-testid="legalcoop-link-copy-btn"
                  >
                    <Copy className="h-4 w-4 mr-1" /> Copier
                  </Button>
                </div>
              </div>
              <p className="text-[11px] text-amber-800 italic">
                ⚠ Ce lien expire selon la politique Stripe (24h par défaut). Envoyez-le rapidement.
              </p>
            </div>
          )}

          <DialogFooter>
            {!linkResult ? (
              <>
                <Button variant="outline" onClick={() => setLinkModal(false)} disabled={linkSubmitting}>
                  Annuler
                </Button>
                <Button
                  onClick={submitPaymentLink}
                  disabled={linkSubmitting}
                  className="bg-indigo-700 hover:bg-indigo-800 text-white"
                  data-testid="legalcoop-link-submit-btn"
                >
                  {linkSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Link2 className="h-4 w-4 mr-1" />}
                  Générer le lien
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => { setLinkModal(false); setLinkResult(null); }}>
                Fermer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* V37.76 — Modal : regles credits LEGALCOOP (Super Admin) */}
      <Dialog open={creditConfigModal} onOpenChange={(o) => { if (!o) setCreditConfigModal(false); }}>
        <DialogContent data-testid="legalcoop-credit-config-modal" className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Coins className="h-5 w-5 text-amber-700" />
              Règles crédits LEGALCOOP (Super Admin)
            </DialogTitle>
            <DialogDescription>
              Activez le paiement par crédits, fixez le taux global (1 crédit = X €),
              les prix en crédits par service et les rôles exonérés.
            </DialogDescription>
          </DialogHeader>

          {creditConfigLoading || !creditConfig ? (
            <div className="py-8 flex items-center justify-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement...
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between border border-slate-200 rounded p-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Paiement par crédits (global)</p>
                  <p className="text-xs text-slate-500">Interrupteur général pour tous les services LEGALCOOP</p>
                </div>
                <Switch
                  checked={!!creditConfig.credits_enabled}
                  onCheckedChange={(v) => setCreditConfig((c) => ({ ...c, credits_enabled: v }))}
                  data-testid="legalcoop-credit-global-toggle"
                />
              </div>

              <div className="border border-slate-200 rounded p-3">
                <label className="text-xs text-slate-600 mb-1 block">
                  Taux global — 1 crédit = X € (TTC)
                </label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={creditConfig.rate_eur_per_credit}
                  onChange={(e) => setCreditConfig((c) => ({ ...c, rate_eur_per_credit: e.target.value }))}
                  className="w-40"
                  data-testid="legalcoop-credit-rate-input"
                />
              </div>

              <div className="border border-slate-200 rounded p-3 space-y-3">
                <p className="text-sm font-semibold text-slate-800">Services</p>
                {Object.entries(creditConfig.services || {}).map(([st, rule]) => (
                  <div key={st} className="flex items-center justify-between gap-3 flex-wrap border-t border-slate-100 pt-2">
                    <div className="flex-1 min-w-[180px]">
                      <p className="text-xs font-semibold text-slate-700">{rule.label || st}</p>
                      <p className="text-[10px] text-slate-500">
                        Prix effectif : <b>{rule.price_credits_effective}</b> crédits
                        {' '}({rule.amount_ttc_eur} € TTC)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="auto"
                        title="Prix en crédits (vide = taux global)"
                        value={rule.price_credits_override ?? ''}
                        onChange={(e) => setServiceRule(st, { price_credits_override: e.target.value === '' ? null : e.target.value })}
                        className="w-24 h-8 text-xs"
                        data-testid={`legalcoop-credit-svc-override-${st}`}
                      />
                      <Switch
                        checked={!!rule.credits_enabled}
                        onCheckedChange={(v) => setServiceRule(st, { credits_enabled: v })}
                        data-testid={`legalcoop-credit-svc-toggle-${st}`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="border border-slate-200 rounded p-3">
                <p className="text-sm font-semibold text-slate-800 mb-2">Rôles exonérés (service gratuit)</p>
                <div className="flex flex-wrap gap-2">
                  {creditRoles.map((role) => {
                    const active = (creditConfig.exempt_roles || []).includes(role);
                    return (
                      <button
                        key={role}
                        type="button"
                        onClick={() => toggleExemptRole(role)}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${
                          active
                            ? 'bg-purple-700 text-white border-purple-900 font-semibold'
                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                        }`}
                        data-testid={`legalcoop-credit-role-${role}`}
                      >
                        {role}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditConfigModal(false)} disabled={creditConfigSaving}>
              Annuler
            </Button>
            <Button
              onClick={saveCreditConfig}
              disabled={creditConfigSaving || creditConfigLoading || !creditConfig}
              className="bg-amber-700 hover:bg-amber-800 text-white"
              data-testid="legalcoop-credit-config-save"
            >
              {creditConfigSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
